/**
 * load core modules
 */
import { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
/* load core modules end */

/**
 * load custom hooks
 */
import { useReducers } from "../hooks/useReducer";
/* load custom hooks end */

/**
 * load actions
 */
import {
  resetAction,
  showToastNotification,
  showModalPopup,
  showRecordingSettingsPanel,
} from "../../App/state/actions";
import {
  setRecordingAction,
  getRecordingAction,
  toggleSettingsPanelAction,
  setTeamRecordingAction,
  SET_RECORDINGS,
  GET_RECORDINGS,
  EDIT_RECORDINGS,
  DELETE_RECORDINGS,
  MANIPULATE_RECORDING_ACTION_TRIGGERED,
  SET_TEAM_RECORDINGS,
} from "../../pages/RecordingsPage/state/actions";
/* load actions end */

import { logDTEvent } from "../../shared/analytics/dynatrace";

/**
 * load utilities
 */
import {
  getRecordingCTAs,
  getRecordingInfo,
  setRecording,
  getRecordingSystemType,
  getCPVRRecordingStatus,
  doesCPVREventRecordingExist,
  isCPVRRecordingInProgress,
  getRecordablePrograms,
  navigateToPVRManager,
  showCPVRConflictModal,
  checkCPVRConflicts,
  isRecordingPendingStateCheck,
  doesCPVRSeriesRecordingExist,
  getTeamRecordingParams,
} from "../utils/recordingHelper";
import { setSessionStorage } from "../utils/sessionStorage";
/* load utilities end */

/**
 * load analytics helper functions
 */
import { trackGenericAction, trackRecordingError } from "../analytics/dataLayer";
import { getCustomContextMetadata, trackConvivaCustomEvent } from "../analytics/media";
/* load analytics helper functions end */

/**
 * load constants
 */
import constants from "../constants";
import { LINK_INFO, ANALYTICS_STORAGE_KEYS, ANALYTICS_EVENT_TYPES, MAPPED_CONTENT_TYPES } from "../constants/analytics";
import recordingConstants from "../constants/recordingConstants";
import { DT_EVENTS } from "../constants/dynatrace";
/* load constants end */

/** declare/destructure constants */
const { MODAL_TYPES, REDUCER_TYPE, CONTENT_ITEM_TYPES } = constants;
const { RECORDING_PARAMS, RECORDING_PACKAGES, MR_RECORDING_FILTER_OPTIONS } = recordingConstants;

/**
 * Custom hook for handling recordings on detail pages
 */
export const useRecordings = (
  currentAndUpcomingSchedules,
  preferredScheduleId,
  contentMetadata,
  setShowRecordingNowBadge,
  vodSubType
) => {
  const [recordingSystemType, setRecordingSystemType] = useState(null);
  const [selectedAssetRecordingInfo, setSelectedAssetRecordingInfo] = useState(null);
  const [recordingActionError, setRecordingActionError] = useState(null);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const setTeamRecordingResponse = useSelector((state) => state?.recording?.setTeamRecordingResponse);
  const hasAllRecordingFetched = useRef(false);

  const recordingEventTypeRef = useRef(null);
  const recordingEventCallbackFuncRef = useRef(null);
  const isRecordingActionInProgress = useRef(false);
  const isTeamRecordingActionInProgress = useRef(false);
  const prevRecordingOnNowResult = useRef();

  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const {
    provider: appProvider,
    userProfile,
    isInHome,
    subscribedChannels,
    featureToggles,
  } = useReducers(REDUCER_TYPE.APP);
  const { isRecordingEnabled, isConvivaAppTrackerEnabled, isDynatraceTrackingEnabled, isTeamRecordingsEnabled } =
    featureToggles;
  const {
    recordingsList,
    getRecordingError,
    setRecordingResponse,
    editRecordingResponse,
    deleteRecordingResponse,
    manipulatedRecordingParams,
  } = useReducers(REDUCER_TYPE.RECORDING);

  const isUserLoggedIn = userProfile?.isLoggedIn ? true : false;

  /** isMR - type: boolean - check if a login user has Media Room recording package, this constant is crucial for recording functions*/
  const isMR = useMemo(
    () => recordingSystemType === RECORDING_PACKAGES.PACKAGE_NAME.LPVRMediaroom_TP,
    [recordingSystemType]
  );

  /** we need to create a recordable program list from schedules, this list can be used on recording functions */
  const recordablePrograms = useMemo(() => {
    if (isRecordingEnabled && currentAndUpcomingSchedules && subscribedChannels) {
      return getRecordablePrograms(currentAndUpcomingSchedules, subscribedChannels);
    }
  }, [currentAndUpcomingSchedules, isRecordingEnabled, subscribedChannels]);

  // list of recording info objects for every program schedule that the user is subscribed to and is airing on a recordable channel
  const programsRecordingInfoList = useMemo(() => {
    if (recordingsList && recordablePrograms) {
      return recordablePrograms.map((schedule) => {
        return getRecordingInfo(isMR, schedule, recordingsList, appProvider?.channelMapID);
      });
    }

    return null;
  }, [recordingsList, isMR, recordablePrograms, appProvider]);

  // Recording info that is associated with the primary recording button
  const primaryRecordingInfo = useMemo(() => {
    if (programsRecordingInfoList) {
      let preferredScheduleRecordingInfo;
      if (preferredScheduleId) {
        preferredScheduleRecordingInfo = programsRecordingInfoList.find(
          (recordingInfo) => recordingInfo.assetToRecord.id === preferredScheduleId
        );
      }

      // Try to find the first schedule that has a recording associated with it already
      let firstExistingRecordingAsset;
      if (isMR) {
        firstExistingRecordingAsset = programsRecordingInfoList.find((recordingInfo) => {
          return (
            recordingInfo.recordingEventScheduledCheck ||
            recordingInfo.recordingSeriesScheduledCheck ||
            recordingInfo.recordingEventConflictCheck ||
            recordingInfo.recordingSeriesConflictCheck
          );
        });
      } else {
        firstExistingRecordingAsset = programsRecordingInfoList.find((recordingInfo) => {
          const assetRecordingStatus = getCPVRRecordingStatus(recordingInfo);
          return (
            doesCPVREventRecordingExist(assetRecordingStatus) || doesCPVRSeriesRecordingExist(assetRecordingStatus)
          );
        });
      }

      return firstExistingRecordingAsset || preferredScheduleRecordingInfo || programsRecordingInfoList[0];
    }

    return null;
  }, [isMR, preferredScheduleId, programsRecordingInfoList]);

  // Primary team recording button CTA details
  const primaryTeamsRecordingCTA = useMemo(() => {
    const sports = primaryRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports;
    if (isTeamRecordingsEnabled && sports?.homeUmId && sports?.guestUmId) {
      if (sports?.homeUmId && sports?.guestUmId) {
        // Check both teams
        const homeTeamRecordingParams = getTeamRecordingParams(
          sports.homeUmId,
          primaryRecordingInfo?.seriesRecordingsList
        );
        const guestTeamRecordingParams = getTeamRecordingParams(
          sports.guestUmId,
          primaryRecordingInfo?.seriesRecordingsList
        );

        // Return the first existing recording params
        if (homeTeamRecordingParams?.recordingExists) {
          return homeTeamRecordingParams;
        } else if (guestTeamRecordingParams?.recordingExists) {
          return guestTeamRecordingParams;
        }
      }
    }

    return null;
  }, [primaryRecordingInfo, isTeamRecordingsEnabled]);

  // Primary recording button CTA details
  const primaryRecordingCTA = useMemo(() => {
    if (primaryRecordingInfo) {
      if (isMR) {
        let isSeriesRecordingSet = false,
          isEventRecordingSet = false;
        if (primaryRecordingInfo.recordingSeriesScheduledCheck) {
          isSeriesRecordingSet = true;
        } else if (primaryRecordingInfo.recordingEventScheduledCheck) {
          isEventRecordingSet = true;
        }

        return getRecordingCTAs(
          true,
          primaryRecordingInfo,
          recordablePrograms,
          primaryRecordingInfo.recordingEventConflictCheck,
          isEventRecordingSet,
          primaryRecordingInfo.recordingSeriesConflictCheck,
          isSeriesRecordingSet
        );
      } else {
        return getRecordingCTAs(false, primaryRecordingInfo, recordablePrograms);
      }
    }
    return null;
  }, [isMR, primaryRecordingInfo, recordablePrograms]);

  // Used to display the loading spinner on the primary recording button, as appropriate
  const showRecordingCTALoading = useMemo(() => {
    if (
      isTeamRecordingsEnabled &&
      manipulatedRecordingParams?.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase()
    ) {
      return true;
    }
    if (
      manipulatedRecordingParams &&
      primaryRecordingInfo &&
      manipulatedRecordingParams.targetAssetInfo?.id === primaryRecordingInfo.assetToRecord?.id
    ) {
      return true;
    }
    return false;
  }, [manipulatedRecordingParams, primaryRecordingInfo, isTeamRecordingsEnabled]);

  /**
   * Determines the appropriate recording toast message to display
   *
   * @param {Object} recordingInfo - recording information used to determine the toast message
   * @param {String} actionType - type of recording action that occurred (set/edit/delete)
   * @returns {String} recordings-specific toast message
   */
  const getRecordingToastMessage = useCallback(
    (recordingInfo, actionType, recordingType) => {
      let toastMsg;
      const teamsRecordingsCheck =
        isTeamRecordingsEnabled && recordingType?.toLowerCase() === RECORDING_PARAMS?.TEAM.toLowerCase();
      if (recordingInfo.code) {
        if (actionType === "set") {
          toastMsg = teamsRecordingsCheck
            ? RECORDING_PARAMS.TEAM_RECORDING_NOT_SET
            : translate(RECORDING_PARAMS.RECORDING_NOT_SET);
        } else if (actionType === "edit") {
          toastMsg = translate(
            RECORDING_PARAMS[
              recordingType === RECORDING_PARAMS.SERIES ? "SERIES_RECORDING_NOT_UPDATED" : "RECORDING_NOT_UPDATED"
            ]
          );
        } else if (actionType === "delete") {
          toastMsg = teamsRecordingsCheck
            ? RECORDING_PARAMS.TEAM_CANNOT_BE_CANCELED
            : translate(RECORDING_PARAMS.COULD_NOT_CANCEL_RECORDING);
        }
      } else {
        if (actionType === "set") {
          toastMsg = teamsRecordingsCheck
            ? translate("team_recording_set")
            : translate(
                RECORDING_PARAMS[recordingType === RECORDING_PARAMS.SERIES ? "SERIES_RECORDING_SET" : "RECORDING_SET"]
              );
        } else if (actionType === "edit") {
          toastMsg = translate(
            RECORDING_PARAMS[
              recordingType === RECORDING_PARAMS.SERIES ? "SERIES_RECORDING_UPDATED" : "RECORDING_UPDATED"
            ]
          );
        } else if (actionType === "delete") {
          toastMsg = teamsRecordingsCheck
            ? translate("team_recording_cancelled")
            : translate(
                RECORDING_PARAMS[
                  recordingType === RECORDING_PARAMS.SERIES ? "SERIES_RECORDING_CANCELLED" : "RECORDING_CANCELLED"
                ]
              );
        }
      }

      return toastMsg;
    },
    [isTeamRecordingsEnabled, translate]
  );

  /**
   * Displays the appropriate recording toast message (if any)
   *
   * @param {Object} recordingInfo - recording information used to determine the toast message
   * @param {Object} manipulatedRecordingParams - refer to manipulateActionTriggered() in RecordingsPage/state/actions.js
   */
  const displayRecordingToast = useCallback(
    (recordingInfo, manipulatedRecordingParams) => {
      if (recordingInfo && manipulatedRecordingParams) {
        const toastMsg = getRecordingToastMessage(
          recordingInfo,
          manipulatedRecordingParams.actionType,
          manipulatedRecordingParams.recordingType
        );
        if (toastMsg) {
          dispatch(showToastNotification(toastMsg));
          // Fire recording start analytics for "set" actions
          if (
            recordingEventTypeRef.current === ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START &&
            manipulatedRecordingParams.actionType === "set" &&
            !recordingInfo.code
          ) {
            const isSeriesEvent = manipulatedRecordingParams.recordingType === RECORDING_PARAMS.SERIES;
            const isTeamRecording =
              manipulatedRecordingParams.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase();
            const teamRecordingPayload = {};
            if (isTeamRecording) {
              const sports =
                selectedAssetRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports ||
                contentMetadata?.metadata?.extendedMetadata?.dlum?.sports;
              const teamUmId = manipulatedRecordingParams.targetAssetInfo?.filterBy?.teamUmId;
              teamRecordingPayload.teamSport = sports?.name || contentMetadata?.metadata?.seriesTitle;
              if (teamUmId === sports?.homeUmId) {
                teamRecordingPayload.teamName = sports?.homeTeam;
              } else if (teamUmId === sports?.guestUmId) {
                teamRecordingPayload.teamName = sports?.guestTeam;
              }
            }
            trackGenericAction(
              ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START,
              {
                isItemLive: true,
                isSeriesEvent,
                isTeamRecording,
                ...teamRecordingPayload,
                mappedContentType: isMR ? MAPPED_CONTENT_TYPES.LPVR : MAPPED_CONTENT_TYPES.CPVR,
                ...recordingInfo?.assetToRecord,
              },
              isDynatraceTrackingEnabled
            );
          }
          // Fire the deferred edit complete analytics callback if one is stored
          if (recordingEventCallbackFuncRef.current) {
            recordingEventCallbackFuncRef.current();
            recordingEventCallbackFuncRef.current = null;
          }
          recordingEventTypeRef.current = null;
        }
      }
    },
    [getRecordingToastMessage, dispatch, isMR, isDynatraceTrackingEnabled, contentMetadata, selectedAssetRecordingInfo]
  );

  // Reset the recordings params associated with an outgoing recording manipulation action (set, edit, delete)
  const resetManipulatedRecordingParams = useCallback(() => {
    dispatch(resetAction(MANIPULATE_RECORDING_ACTION_TRIGGERED, "recordingParams"));
  }, [dispatch]);

  // Reset the response data from setting a recording
  const resetSetRecordingResponse = useCallback(() => {
    dispatch(resetAction(SET_RECORDINGS, "content"));
  }, [dispatch]);

  // Reset the response error from fetching recordings
  const resetGetRecordingError = useCallback(() => {
    dispatch(resetAction(GET_RECORDINGS, "error"));
  }, [dispatch]);

  // Reset the response data from editing a recording
  const resetEditRecordingResponse = useCallback(() => {
    dispatch(resetAction(EDIT_RECORDINGS, "content"));
  }, [dispatch]);

  // Reset the response data from deleting a recording
  const resetDeleteRecordingResponse = useCallback(() => {
    dispatch(resetAction(DELETE_RECORDINGS, "content"));
  }, [dispatch]);

  // Reset the response data from setting a recording
  const resetSetTeamRecordingResponse = useCallback(() => {
    dispatch(resetAction(SET_TEAM_RECORDINGS, "content"));
  }, [dispatch]);

  /**
   * Resets the values used to support recording CRUD action flows
   */
  const resetRecordingActionValues = useCallback(() => {
    isRecordingActionInProgress.current = false;
    isTeamRecordingActionInProgress.current = false;
    resetSetRecordingResponse();
    resetSetTeamRecordingResponse();
    resetEditRecordingResponse();
    resetDeleteRecordingResponse();
    resetManipulatedRecordingParams();
    resetGetRecordingError();
    setRecordingActionError(null);
  }, [
    resetDeleteRecordingResponse,
    resetEditRecordingResponse,
    resetGetRecordingError,
    resetManipulatedRecordingParams,
    resetSetRecordingResponse,
    resetSetTeamRecordingResponse,
  ]);

  /**
   * Fetch the user's recordings and save the response in redux
   * @param {Object} recordingParamsMR - MR specific recording params used for set/edit/delete recording when initial attempt reports PENDING status
   * @param {Boolean} fetchingAfterAction - flag used to differentiate between initial fetch and post-action fetches
   */
  const fetchRecordings = useCallback(
    async (recordingParamsMR = null) => {
      if (
        isTeamRecordingsEnabled &&
        manipulatedRecordingParams?.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase()
      ) {
        if (isTeamRecordingActionInProgress.current) return;
      } else {
        if (isRecordingActionInProgress.current) return;
      }

      if (
        isTeamRecordingsEnabled &&
        manipulatedRecordingParams?.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase()
      ) {
        isTeamRecordingActionInProgress.current = true;
      } else {
        isRecordingActionInProgress.current = true;
      }

      let recordingsResponse;
      if (isMR) {
        // Media Room specific call
        recordingsResponse = await dispatch(
          getRecordingAction(
            appProvider,
            true,
            null,
            null,
            null,
            MR_RECORDING_FILTER_OPTIONS.SCHEDULED_PAGE_FILTERS,
            recordingParamsMR
          )
        );
      } else {
        recordingsResponse = await dispatch(
          getRecordingAction(appProvider, false, true, null, [RECORDING_PARAMS.EVENT, RECORDING_PARAMS.SERIES])
        );
      }
      // After fetch completes, reset the in-progress flag
      if (
        isTeamRecordingsEnabled &&
        recordingsResponse &&
        manipulatedRecordingParams?.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase()
      ) {
        displayRecordingToast(setTeamRecordingResponse || deleteRecordingResponse, manipulatedRecordingParams);
        resetManipulatedRecordingParams();
        isTeamRecordingActionInProgress.current = false;
        hasAllRecordingFetched.current = false;
        resetDeleteRecordingResponse();
      }
    },
    [
      isTeamRecordingsEnabled,
      isMR,
      dispatch,
      appProvider,
      displayRecordingToast,
      setTeamRecordingResponse,
      deleteRecordingResponse,
      manipulatedRecordingParams,
      resetManipulatedRecordingParams,
      resetDeleteRecordingResponse,
    ]
  );

  /**
   * A callback function which will be triggered after a recording has been updated
   * @param {Object} recordingItem
   * @param {Boolean} isItemLive
   * @param {Boolean} isSeriesEvent
   * @param {String} mappedContentType live|svod|tvod|cpvr|lpvr
   * @param {String} analyticsEventType analytics event type
   * @param {String} toolSelections a toolSelection string with format: 'key:{selection},key:{selection}...'
   * @param {Object} error an error object
   */
  const trackRecordingUpdateEventCallback = useCallback(
    ({
      recordingItem,
      isItemLive,
      isSeriesEvent,
      isTeamRecording,
      mappedContentType,
      analyticsEventType,
      toolSelections,
      error,
    }) => {
      if (error) {
        // Triggers a stop|delete recording error.
        // The error of start a recording comes from setRecordingResponse data which is handled via useEffect.
        trackRecordingError(
          analyticsEventType,
          error?.message,
          {
            errorCode: error?.code,
          },
          isDynatraceTrackingEnabled
        );
      } else {
        recordingEventCallbackFuncRef.current = () => {
          // For team recordings, resolve sport name and team name from the sports metadata.
          // Uses a fallback chain: selectedAsset > primaryRecording > contentMetadata,
          // then matches teamUmId to determine home vs guest team.
          const teamRecordingPayload = {};
          if (isTeamRecording) {
            const sports =
              selectedAssetRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports ||
              primaryRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports ||
              contentMetadata?.metadata?.extendedMetadata?.dlum?.sports;
            const teamUmId = manipulatedRecordingParams?.targetAssetInfo?.filterBy?.teamUmId;
            teamRecordingPayload.teamSport = sports?.name || contentMetadata?.metadata?.seriesTitle;
            if (teamUmId === sports?.homeUmId) {
              teamRecordingPayload.teamName = sports?.homeTeam;
            } else if (teamUmId === sports?.guestUmId) {
              teamRecordingPayload.teamName = sports?.guestTeam;
            }
          }
          trackGenericAction(
            analyticsEventType,
            {
              isItemLive,
              isSeriesEvent,
              isTeamRecording,
              toolSelections,
              mappedContentType,
              ...teamRecordingPayload,
              ...(isMR
                ? {
                    ...contentMetadata,
                    contentId: recordingItem?.metadata?.contentId,
                    channel: {
                      callLetter: selectedAssetRecordingInfo?.callLetter,
                      channelName: selectedAssetRecordingInfo?.channelName,
                      channelId: selectedAssetRecordingInfo?.mediaRoom?.channelId,
                      programId: selectedAssetRecordingInfo?.mediaRoom?.programId,
                    },
                  }
                : primaryRecordingInfo?.assetToRecord || recordingItem),
            },
            isDynatraceTrackingEnabled
          );
        };

        if (
          analyticsEventType === ANALYTICS_EVENT_TYPES.VIDEO_RECORD_SERIES_EDIT_COMPLETE ||
          analyticsEventType === ANALYTICS_EVENT_TYPES.VIDEO_RECORD_EPISODE_EDIT_COMPLETE ||
          analyticsEventType === ANALYTICS_EVENT_TYPES.VIDEO_RECORD_TEAM_EDIT_COMPLETE
        ) {
          // At this moment, the result of editing a recording is unknown, but a toastMessage will show up on the screen according to the result of editing a recording.
          // Thus set the recordingEventType here, and then trigger the event based on the toastMessage later.
          recordingEventTypeRef.current = analyticsEventType;
        } else {
          // The result of cancel|delete a recording is ready at this moment
          recordingEventCallbackFuncRef.current();
        }
      }
    },
    [
      isMR,
      contentMetadata,
      selectedAssetRecordingInfo,
      primaryRecordingInfo,
      manipulatedRecordingParams,
      isDynatraceTrackingEnabled,
    ]
  );

  /** Memoized showRecordingSettingHandler, function object will only change when showRecordingSettingsPanel changes */
  const showRecordingSettingHandler = useCallback(
    (recordingInfo, typeOfRecording) => {
      if (vodSubType === CONTENT_ITEM_TYPES.episode) {
        const isSeriesEvent = typeOfRecording === RECORDING_PARAMS.SERIES;
        trackRecordingUpdateEventCallback({
          isSeriesEvent,
          isItemLive: true,
          recordingItem: recordingInfo?.assetToRecord,
          mappedContentType: MAPPED_CONTENT_TYPES.LIVE,
          analyticsEventType: isSeriesEvent
            ? ANALYTICS_EVENT_TYPES.VIDEO_RECORD_SERIES_EDIT_START
            : ANALYTICS_EVENT_TYPES.VIDEO_RECORD_EPISODE_EDIT_START,
        });

        dispatch(
          showRecordingSettingsPanel(
            typeOfRecording,
            recordingInfo,
            !isMR ? recordablePrograms : null,
            false,
            (recordingData) => trackRecordingUpdateEventCallback({ recordingInfo, ...recordingData })
          )
        );
        dispatch(toggleSettingsPanelAction(true));
      } else {
        trackGenericAction(
          ANALYTICS_EVENT_TYPES.VIDEO_RECORD_EPISODE_EDIT_START,
          {
            isItemLive: true,
            isSeriesEvent: false,
            ...(isMR
              ? {
                  ...contentMetadata,
                  channel: {
                    callLetter: selectedAssetRecordingInfo?.callLetter,
                    channelName: selectedAssetRecordingInfo?.channelName,
                    channelId: selectedAssetRecordingInfo?.mediaRoom?.channelId,
                    programId: selectedAssetRecordingInfo?.mediaRoom?.programId,
                  },
                }
              : recordingInfo?.assetToRecord),
          },
          isDynatraceTrackingEnabled
        );
        if (isConvivaAppTrackerEnabled) {
          trackConvivaCustomEvent(
            ANALYTICS_EVENT_TYPES.VIDEO_RECORD_EPISODE_EDIT_START,
            getCustomContextMetadata(contentMetadata, userProfile, appProvider, isInHome)
          );
        }
        dispatch(
          showRecordingSettingsPanel(
            typeOfRecording,
            recordingInfo,
            !isMR ? recordablePrograms : null,
            false,
            trackRecordingUpdateEventCallback
          )
        );
      }
    },
    [
      contentMetadata,
      selectedAssetRecordingInfo,
      isMR,
      dispatch,
      recordablePrograms,
      trackRecordingUpdateEventCallback,
      appProvider,
      isInHome,
      userProfile,
      isConvivaAppTrackerEnabled,
      vodSubType,
      isDynatraceTrackingEnabled,
    ]
  );

  /**
   * Schedule a recording and save the response in redux
   *
   * @param {String} typeOfRecording - the recording type (event)
   * @param {Object} recordingInfo - recording information used to set the recording
   */
  const scheduleRecording = useCallback(
    (typeOfRecording, recordingInfo) => {
      if (recordingInfo) {
        recordingEventTypeRef.current = ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START;
        setRecording(typeOfRecording, appProvider, recordingInfo.assetToRecord, setRecordingAction, isMR, dispatch);
      }
      logDTEvent(DT_EVENTS.RECORDING_START, null, isDynatraceTrackingEnabled);
    },
    [appProvider, isMR, dispatch, isDynatraceTrackingEnabled]
  );

  const scheduleTeamRecording = useCallback(
    async (teamRecordingsParams) => {
      isRecordingActionInProgress.current = false;
      if (!teamRecordingsParams || isTeamRecordingActionInProgress.current) return;
      isTeamRecordingActionInProgress.current = true;
      recordingEventTypeRef.current = ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START;

      const response = await dispatch(setTeamRecordingAction(appProvider, teamRecordingsParams));
      if (response) {
        resetSetTeamRecordingResponse();
      }
    },
    [appProvider, dispatch, resetSetTeamRecordingResponse]
  );

  /**
   * Opens the modal used for deleting recorded recordings or cancelling scheduled/in progress recordings.
   *
   * @param {Object} recordingInfo Recording information
   * @param {Object} programDetails Program information
   */
  const openCancelRecordingModal = useCallback(
    (recordingInfo, programDetails, existingTeamInfo) => {
      const modalContent = {
        recordingInfo,
        programDetails,
        isRecordingRecorded: false,
        recordingType: existingTeamInfo?.length ? RECORDING_PARAMS.TEAM_EVENT : RECORDING_PARAMS.EVENT,
        isMR: false,
      };
      dispatch(showModalPopup(MODAL_TYPES.RECORDING, modalContent));
    },
    [dispatch]
  );

  const closeRecordingModal = () => {
    setShowRecordingModal(false);
  };

  /**
   * @param {Object} recordingInfo - Object containing response of particular asset from recordingsList object to compare and edit the recordings.
   * @param {String} typeOfRecording - EVENT
   */
  const editRecordingHandler = (recordingInfo, typeOfRecording) => {
    showRecordingSettingHandler(recordingInfo, typeOfRecording);
  };

  /** Click event handler replaces the recording modal popup*/
  const handleRecordingAction = useCallback(
    (event, recordingInfo = primaryRecordingInfo) => {
      event.stopPropagation();
      if (recordingInfo) {
        setSelectedAssetRecordingInfo(recordingInfo);
        if (isMR) {
          const hasEventRecording =
            vodSubType === CONTENT_ITEM_TYPES.movie
              ? recordingInfo.recordingEventScheduledCheck
              : recordingInfo.recordingEventScheduledCheck && !recordingInfo.recordingSeriesScheduledCheck;
          if (hasEventRecording) {
            setShowRecordingModal(true);
          } else if (
            vodSubType === CONTENT_ITEM_TYPES.movie &&
            !recordingInfo.recordingEventConflictCheck &&
            !recordingInfo.recordingEventScheduledCheck
          ) {
            scheduleRecording(RECORDING_PARAMS.EVENT, recordingInfo);
          } else {
            setShowRecordingModal(true);
          }
        } else {
          const cpvrRecordingStatus = getCPVRRecordingStatus(recordingInfo);
          const isRecordingInProgress = isCPVRRecordingInProgress(cpvrRecordingStatus);
          if (doesCPVREventRecordingExist(cpvrRecordingStatus)) {
            if (!isTeamRecordingsEnabled && isRecordingInProgress) {
              // Show cancel modal
              openCancelRecordingModal(recordingInfo, recordingInfo.assetToRecord);
            } else {
              setShowRecordingModal(true);
            }
          } else {
            if (vodSubType === CONTENT_ITEM_TYPES.episode) {
              // If no series recording, no episode recording, and no single event recording exists, we show the recording modal
              // so the user can schedule either a single event recording or series recording (which should create an episode recording)
              // for this episode.
              // If a series recording exists for the show that this episode belongs to but there is no episode recording
              // for this specific episode, we do not allow the user to edit the series recording from this episode detail page.
              // Instead, we show the recording modal so the user can schedule a recording for this episode as a single event or
              // schedule the series recording.
              // TODO: trying to schedule the series recording in this last scenario will do nothing since the series recording already
              // exists, so we will need some guidance on what can be done for this (should we remove the series button in the modal? is
              // there a way to add episode recordings to an existing series recording?)
              setShowRecordingModal(true);
            } else {
              scheduleRecording(RECORDING_PARAMS.EVENT, recordingInfo);
            }
          }
        }
      }
    },
    [isMR, openCancelRecordingModal, scheduleRecording, primaryRecordingInfo, vodSubType, isTeamRecordingsEnabled]
  );

  useEffect(() => {
    if (isUserLoggedIn) {
      setRecordingSystemType(getRecordingSystemType(userProfile));
    }
  }, [isUserLoggedIn, userProfile]);

  /**
   * This useEffect is used to deal with errors that occur during recording CRUD actions
   */
  useEffect(() => {
    if ((getRecordingError || recordingActionError) && manipulatedRecordingParams) {
      displayRecordingToast(getRecordingError || recordingActionError, manipulatedRecordingParams);
      resetRecordingActionValues();
    }
  }, [
    displayRecordingToast,
    getRecordingError,
    manipulatedRecordingParams,
    recordingActionError,
    resetRecordingActionValues,
  ]);

  /**
   * This useEffect is used after we have re-fetched recordings following a set/edit/delete recording action.
   */
  useEffect(() => {
    if (programsRecordingInfoList && manipulatedRecordingParams && isRecordingActionInProgress.current) {
      // Can't use the selectedAssetRecordingInfo as-is, need to update it with the latest recording state checks/values
      const selectedRecordingInfo = programsRecordingInfoList.find((recordingInfo) => {
        return recordingInfo?.assetToRecord?.id === selectedAssetRecordingInfo?.assetToRecord?.id;
      });
      setSelectedAssetRecordingInfo(selectedRecordingInfo);

      if (
        isMR &&
        (selectedRecordingInfo?.recordingSeriesConflictCheck || selectedRecordingInfo?.recordingEventConflictCheck)
      ) {
        setShowRecordingModal(true);
      } else {
        displayRecordingToast(selectedRecordingInfo, manipulatedRecordingParams);
      }

      resetRecordingActionValues();
    }
  }, [
    displayRecordingToast,
    programsRecordingInfoList,
    isMR,
    manipulatedRecordingParams,
    resetRecordingActionValues,
    selectedAssetRecordingInfo,
  ]);

  const handleRecordingResponse = useCallback(
    (response, isTeamAction) => {
      if (!response) return;

      if (isMR) {
        if (response.code) {
          // Error occurred on MR
          setRecordingActionError(response);
        } else {
          fetchRecordings(isRecordingPendingStateCheck(response.content) ? response.recordingParams : null);
        }
      } else {
        if (response.code) {
          if (checkCPVRConflicts(response)) {
            // show popup modal for recording conflict error
            showCPVRConflictModal(
              () => {
                navigateToPVRManager(true);
                setSessionStorage(
                  ANALYTICS_STORAGE_KEYS.LINK,
                  `${LINK_INFO.MANAGE_RECORDINGS};${LINK_INFO.RECORDING_PROMPT}`
                );
              },
              () => {
                //It is yet to be decided what needs to be displayed when there is an error on the team recording action.
                const toastMessage =
                  isTeamRecordingsEnabled && isTeamAction
                    ? RECORDING_PARAMS.TEAM_RECORDING_NOT_SET
                    : translate("recording_not_set");
                showToastNotification(toastMessage);
              },
              MODAL_TYPES.ERROR,
              showModalPopup
            );
            resetRecordingActionValues();
          } else {
            // show generic 'not set' toast notification for all other errors
            setRecordingActionError(response);
          }
        } else {
          // No error in the response, refresh our recordings list
          if (isTeamRecordingsEnabled && isTeamAction) {
            if (!hasAllRecordingFetched.current) {
              fetchRecordings(null);
              hasAllRecordingFetched.current = true;
            }
          } else {
            fetchRecordings(null);
          }
        }
      }
    },
    [isMR, fetchRecordings, resetRecordingActionValues, isTeamRecordingsEnabled, translate]
  );

  /**
   * This useEffect is used after we have attempted to schedule a recording
   */
  useEffect(() => {
    isRecordingActionInProgress.current = false;
    isTeamRecordingActionInProgress.current = false;
    if (
      (setRecordingResponse && isRecordingActionInProgress.current === false) ||
      (setTeamRecordingResponse && isTeamRecordingActionInProgress.current === false) ||
      (manipulatedRecordingParams?.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase() &&
        deleteRecordingResponse &&
        isTeamRecordingActionInProgress.current === false)
    ) {
      if (isMR && setRecordingResponse) {
        handleRecordingResponse(setRecordingResponse, false);
      } else if (setRecordingResponse) {
        handleRecordingResponse(setRecordingResponse, false);
      } else if (setTeamRecordingResponse) {
        handleRecordingResponse(setTeamRecordingResponse, true);
      } else if (
        manipulatedRecordingParams?.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase() &&
        deleteRecordingResponse
      ) {
        handleRecordingResponse(deleteRecordingResponse, true);
        hasAllRecordingFetched.current = true;
      }
    }
  }, [
    setRecordingResponse,
    setTeamRecordingResponse,
    isRecordingActionInProgress,
    isTeamRecordingActionInProgress,
    handleRecordingResponse,
    isMR,
    deleteRecordingResponse,
    hasAllRecordingFetched,
    manipulatedRecordingParams?.recordingType,
  ]);

  /**
   * This useEffect is used after we have attempted to edit a recording
   */
  useEffect(() => {
    if (editRecordingResponse && isRecordingActionInProgress.current === false) {
      if (editRecordingResponse.code) {
        // Error occurred
        setRecordingActionError(editRecordingResponse);
      } else {
        fetchRecordings(
          isMR && isRecordingPendingStateCheck(editRecordingResponse.content)
            ? editRecordingResponse.recordingParams
            : null,
          true
        );
      }
    }
  }, [editRecordingResponse, isMR, fetchRecordings]);

  /**
   * This useEffect is used after we have attempted to cancel a recording
   */
  useEffect(() => {
    if (
      deleteRecordingResponse &&
      isRecordingActionInProgress.current === false &&
      manipulatedRecordingParams?.recordingType?.toLowerCase() !== RECORDING_PARAMS.TEAM.toLowerCase()
    ) {
      if (deleteRecordingResponse.code) {
        // Error occurred
        setRecordingActionError(deleteRecordingResponse);
      } else {
        // Nothing useful returned in the MR delete response, so we assume its pending
        fetchRecordings(isMR ? deleteRecordingResponse.recordingParams : null, true);
      }
    }
  }, [deleteRecordingResponse, fetchRecordings, isMR, manipulatedRecordingParams?.recordingType]);

  useEffect(() => {
    if (setShowRecordingNowBadge) {
      let result = false;
      if (primaryRecordingInfo) {
        if (isMR) {
          result =
            primaryRecordingInfo.recordingNowEventCheck || primaryRecordingInfo.recordingNowSeriesCheck ? true : false;
        } else {
          result = isCPVRRecordingInProgress(getCPVRRecordingStatus(primaryRecordingInfo));
        }
      }

      if (result !== prevRecordingOnNowResult.current) {
        setShowRecordingNowBadge(result);
      }

      prevRecordingOnNowResult.current = result;
    }
  }, [isMR, primaryRecordingInfo, setShowRecordingNowBadge]);

  useEffect(() => {
    return () => resetRecordingActionValues();
  }, [resetRecordingActionValues]);

  return {
    isMR,
    selectedAssetRecordingInfo,
    showRecordingModal,
    handleRecordingAction,
    showRecordingCTALoading,
    primaryRecordingCTA,
    closeRecordingModal,
    scheduleRecording,
    scheduleTeamRecording,
    editRecordingHandler,
    openCancelRecordingModal,
    primaryTeamsRecordingCTA,
  };
};
